import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, hasPermission } from './contexts/AuthContext';
import { AppContextProvider } from './contexts/AppContext';
import Login from './pages/Login';
import DashboardLayout from './components/DashboardLayout';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { PageSkeleton } from './components/Skeletons';

// Route-level code splitting: the initial bundle no longer carries recharts,
// jspdf or the zxing barcode engine.
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const WarehouseDashboard = lazy(() => import('./pages/WarehouseDashboard'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Products = lazy(() => import('./pages/Products'));
const ProductForm = lazy(() => import('./pages/ProductForm'));
const Picklist = lazy(() => import('./pages/Picklist'));
const StockInward = lazy(() => import('./pages/StockInward'));
const ManageLists = lazy(() => import('./pages/ManageLists'));
const Shipments = lazy(() => import('./pages/Shipments'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Warehouses = lazy(() => import('./pages/Warehouses'));
const Users = lazy(() => import('./pages/Users'));
const UserForm = lazy(() => import('./pages/UserForm'));
const ActivityLogs = lazy(() => import('./pages/ActivityLogs'));
const Categories = lazy(() => import('./pages/Categories'));
const SubCategories = lazy(() => import('./pages/SubCategories'));
const BulkCategoryMapping = lazy(() => import('./pages/BulkCategoryMapping'));
const BulkProductCreation = lazy(() => import('./pages/BulkProductCreation'));
const BulkInventoryUpdate = lazy(() => import('./pages/BulkInventoryUpdate'));
const ProductGroups = lazy(() => import('./pages/ProductGroups'));
const ProductGroupDetail = lazy(() => import('./pages/ProductGroupDetail'));
const ExportData = lazy(() => import('./pages/ExportData'));
const ApiKeys = lazy(() => import('./pages/ApiKeys'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const ReorderReport = lazy(() => import('./pages/ReorderReport'));
const ChannelMapping = lazy(() => import('./pages/ChannelMapping'));
const DispatchSync = lazy(() => import('./pages/DispatchSync'));
const SsoCallback = lazy(() => import('./pages/SsoCallback'));

const ProtectedRoute = ({ children, minRole }) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (minRole && !hasPermission(user?.role, minRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { user, isAuthenticated } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (hasPermission(user.role, 'admin')) return '/dashboard/admin';
    return '/inventory';
  };

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated() ? <Navigate to={getDefaultRoute()} replace /> : <Login />
      } />

      <Route path="/sso" element={
        <Suspense fallback={<div className="min-h-screen" />}><SsoCallback /></Suspense>
      } />

      <Route element={
        <ProtectedRoute>
          <AppContextProvider>
            <DashboardLayout />
          </AppContextProvider>
        </ProtectedRoute>
      }>
        <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />

        <Route path="/dashboard/admin" element={
          <ProtectedRoute minRole="admin"><AdminDashboard /></ProtectedRoute>
        } />
        <Route path="/dashboard/warehouse" element={
          <ProtectedRoute minRole="wh_manager"><WarehouseDashboard /></ProtectedRoute>
        } />

        <Route path="/inventory" element={<Inventory />} />
        <Route path="/products" element={
          <ProtectedRoute minRole="wh_manager"><Products /></ProtectedRoute>
        } />
        <Route path="/products/new" element={
          <ProtectedRoute minRole="wh_manager"><ProductForm /></ProtectedRoute>
        } />
        <Route path="/products/edit/:id" element={
          <ProtectedRoute minRole="admin"><ProductForm /></ProtectedRoute>
        } />
        <Route path="/product-groups" element={
          <ProtectedRoute minRole="admin"><ProductGroups /></ProtectedRoute>
        } />
        <Route path="/product-groups/:id" element={
          <ProtectedRoute minRole="admin"><ProductGroupDetail /></ProtectedRoute>
        } />

        <Route path="/picklist" element={<Picklist />} />
        <Route path="/stock-inward" element={
          <ProtectedRoute minRole="wh_manager"><StockInward /></ProtectedRoute>
        } />
        <Route path="/manage-lists" element={
          <ProtectedRoute minRole="wh_manager"><ManageLists /></ProtectedRoute>
        } />
        <Route path="/intransit" element={<Shipments />} />
        <Route path="/transactions" element={
          <ProtectedRoute minRole="wh_manager"><Transactions /></ProtectedRoute>
        } />
        <Route path="/warehouses" element={
          <ProtectedRoute minRole="admin"><Warehouses /></ProtectedRoute>
        } />

        <Route path="/users" element={
          <ProtectedRoute minRole="super_admin"><Users /></ProtectedRoute>
        } />
        <Route path="/users/new" element={
          <ProtectedRoute minRole="super_admin"><UserForm /></ProtectedRoute>
        } />
        <Route path="/users/edit/:id" element={
          <ProtectedRoute minRole="super_admin"><UserForm /></ProtectedRoute>
        } />

        <Route path="/logs" element={
          <ProtectedRoute minRole="wh_manager"><ActivityLogs /></ProtectedRoute>
        } />

        <Route path="/categories" element={
          <ProtectedRoute minRole="admin"><Categories /></ProtectedRoute>
        } />
        <Route path="/subcategories" element={
          <ProtectedRoute minRole="wh_manager"><SubCategories /></ProtectedRoute>
        } />
        <Route path="/bulk-category" element={
          <ProtectedRoute minRole="admin"><BulkCategoryMapping /></ProtectedRoute>
        } />
        <Route path="/bulk-products" element={
          <ProtectedRoute minRole="admin"><BulkProductCreation /></ProtectedRoute>
        } />
        <Route path="/bulk-inventory" element={
          <ProtectedRoute minRole="admin"><BulkInventoryUpdate /></ProtectedRoute>
        } />
        <Route path="/export" element={
          <ProtectedRoute minRole="admin"><ExportData /></ProtectedRoute>
        } />
        <Route path="/settings/api-keys" element={
          <ProtectedRoute minRole="admin"><ApiKeys /></ProtectedRoute>
        } />
        <Route path="/suppliers" element={
          <ProtectedRoute minRole="admin"><Suppliers /></ProtectedRoute>
        } />
        <Route path="/reorder-report" element={
          <ProtectedRoute minRole="admin"><ReorderReport /></ProtectedRoute>
        } />
        <Route path="/channel-mapping" element={
          <ProtectedRoute minRole="admin"><ChannelMapping /></ProtectedRoute>
        } />
        <Route path="/dispatch-sync" element={
          <ProtectedRoute minRole="admin"><DispatchSync /></ProtectedRoute>
        } />
      </Route>

      <Route path="*" element={
        <div className="min-h-screen empty-state">
          <p className="eyebrow">404</p>
          <h1 className="text-xl font-semibold text-slate-900">Page not found</h1>
          <a href="/" className="btn-primary">Back to app</a>
        </div>
      } />
    </Routes>
  );
}

function AppWithPWA() {
  return (
    <Suspense fallback={<div className="min-h-screen p-6"><PageSkeleton /></div>}>
      <App />
      <PWAInstallPrompt />
    </Suspense>
  );
}

export default AppWithPWA;
